import "./LineUps.css";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

import { useLayoutEffect, useRef, useState } from "react";

import { useLineUpsData, useMediaQuery } from "../../hooks";
import { DataLoader } from "../DataLoader";
import { ErrorScreen } from "../ErrorScreen";
import FormationGrid from "../FormationGrid/FormationGrid";
import { LineupsList } from "../LineUpList";
import { SbsAnalyticsService } from "../../utils";
import { useAppStore } from "../../stores";

const DESKTOP_DESIGN_WIDTH = 400;
const DESKTOP_MIN_HEIGHT = 160;

export function LineUps() {
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const wrapperRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [scaledHeight, setScaledHeight] = useState<number | undefined>(undefined);

  const {
    teamAdata,
    teamBdata,
    lineups,
    layout,
    teamShirtColor,
    teamTextColor,
    selectedTab,
    setSelectedTab,
    isLoading,
    error,
    refetch,
    currentGroupedData,
    formationUsed,
    teamOfficials,
  } = useLineUpsData();

  useLayoutEffect(() => {
    if (!isDesktop || isLoading || error) {
      setScale(1);
      setScaledHeight(undefined);
      return;
    }
    const wrapper = wrapperRef.current;
    const inner = innerRef.current;
    const parent = wrapper?.parentElement;
    if (!wrapper || !inner || !parent) return;
    const update = () => {
      const cs = getComputedStyle(parent);
      const padX =
        (parseFloat(cs.paddingLeft) || 0) +
        (parseFloat(cs.paddingRight) || 0);
      const w = parent.clientWidth - padX;
      if (w <= 0) return;
      const next = Math.min(w / DESKTOP_DESIGN_WIDTH, 1);
      const h = Math.max(inner.scrollHeight, inner.offsetHeight);
      setScale(next);
      setScaledHeight(h * next);
    };
    update();
    const parentRO = new ResizeObserver(update);
    parentRO.observe(parent);
    const innerRO = new ResizeObserver(update);
    innerRO.observe(inner);
    const mo = new MutationObserver(update);
    mo.observe(inner, { childList: true, subtree: true, attributes: true });
    return () => {
      parentRO.disconnect();
      innerRO.disconnect();
      mo.disconnect();
    };
  }, [isDesktop, isLoading, error]);

  if (isLoading) {
    return (
      <DataLoader
        customStyle={{
          height: "90dvh",
        }}
      />
    );
  }

  const desktopWrapperStyle = isDesktop
    ? {
        width: "100%",
        maxWidth: "100%",
        height: scaledHeight,
        minHeight: DESKTOP_MIN_HEIGHT,
        overflow: "hidden" as const,
      }
    : undefined;
  const desktopInnerStyle = isDesktop
    ? {
        width: DESKTOP_DESIGN_WIDTH,
        transform: `scale(${scale})`,
        transformOrigin: "top left" as const,
      }
    : undefined;

  return (
    <div className="line-ups-container">
      <div className="teams-tab">
        <button
          className={`team-tab home-team ${
            selectedTab === "home" ? "selected" : ""
          }`}
          onClick={() => {
            if (selectedTab !== "home") {
              SbsAnalyticsService.trackEvent("team_selected", {
                match_id: useAppStore.getState().matchData?.id || "",
                selected_team_id: teamAdata?.contestantId || "",
                selected_team_name: teamAdata?.teamName || "",
              });
            }
            setSelectedTab("home");
          }}
        >
          <span>{teamAdata.teamCode}</span>
          <span>
            {teamAdata.formationUsed.length
              ? `(${teamAdata.formationUsed})`
              : ""}
          </span>
        </button>
        <button
          className={`team-tab away-team ${
            selectedTab === "away" ? "selected" : ""
          }`}
          onClick={() => {
            if (selectedTab !== "away") {
              SbsAnalyticsService.trackEvent("team_selected", {
                match_id: useAppStore.getState().matchData?.id || "",
                selected_team_id: teamBdata?.contestantId || "",
                selected_team_name: teamBdata?.teamName || "",
              });
            }
            setSelectedTab("away");
          }}
        >
          <span>{teamBdata.teamCode}</span>
          <span>
            {teamBdata.formationUsed.length
              ? `(${teamBdata.formationUsed})`
              : ""}
          </span>
        </button>
      </div>
      {error ? (
        <div className="line-ups-error">
          <ErrorScreen refetch={refetch} />
        </div>
      ) : (
        <div ref={wrapperRef} style={desktopWrapperStyle}>
          <div ref={innerRef} style={desktopInnerStyle}>
            <FormationGrid
              layout={layout}
              lineups={lineups}
              shirtColor={teamShirtColor || "#ff0000"}
              textColor={teamTextColor || "#fff"}
              currentGroupedEventData={currentGroupedData}
              formationUsed={formationUsed}
            />
            <LineupsList
              players={lineups}
              shirtColor={teamShirtColor || "#ff0000"}
              textColor={teamTextColor || "#fff"}
              currentGroupedEventData={currentGroupedData}
              teamOfficials={teamOfficials}
            />
          </div>
        </div>
      )}
    </div>
  );
}
